import {
  ApplicationConfig,
  provideZoneChangeDetection,
  isDevMode,
  provideAppInitializer,
  inject,
  PLATFORM_ID,
} from "@angular/core";
import { provideRouter, withHashLocation, withPreloading } from "@angular/router";
import { CustomPreloadingStrategy } from "./core/strategies/custom-preloading.strategy";
import { routes } from "./app.routes";
import {
  provideHttpClient,
  withFetch,
  withInterceptors,
} from "@angular/common/http";
import { provideAnimationsAsync } from "@angular/platform-browser/animations/async";
import { ErrorHandlerInterceptor } from "./core/interceptors/error-handler.interceptor";
import { API_URL } from "../environments/environment";
import { UrlPatcherInterceptor } from "./core/interceptors/url-patcher.interceptor";
import { HttpConfigInterceptor } from "./core/interceptors/http-config.interceptor";
import { SpinnerInterceptor } from "./core/interceptors/spinner.interceptor";
import { provideServiceWorker } from "@angular/service-worker";
import { provideStore } from "@ngrx/store";
import { provideEffects } from "@ngrx/effects";
import { provideStoreDevtools } from "@ngrx/store-devtools";
import { usuarioAuthReducer } from "./store/reducers/usuario-auth.reducer";
import { permisosReducer } from "./store/reducers/permisos.reducer";
import { ivaReducer } from "./store/reducers/tipoResponsableIva.reducer";
import { documentoReducer } from "./store/reducers/tipoDocumento.reducer";
import { paisReducer } from "./store/reducers/paises.reducer";
import { formaPagosReducer } from "./store/reducers/formasPago.reducer";
import { firebaseConfig } from "../environments/environment";
import { provideFirebaseApp, initializeApp } from "@angular/fire/app";
import { provideMessaging, getMessaging } from "@angular/fire/messaging";
import { empresaActualReducer } from "./store/reducers/empresa-actual.reducer";
import { comprobanteReducer } from "./store/reducers/tipoComprobante.reducer";
import { LOCALE_ID } from '@angular/core';
import { isPlatformBrowser, registerLocaleData } from '@angular/common';
import localeEs from '@angular/common/locales/es';
import { parametrosGeneralesReducer } from "./store/reducers/parametrosGenerales.reducer";
import { tipoMonedaReducer } from "./store/reducers/tipoMoneda.reducer";
import { provinciaReducer } from "./store/reducers/provincias.reducer";
import { actividadReducer } from "./store/reducers/tipoActividad.reducer";
import { operacionTipoIvaReducer } from "./store/reducers/operacionTipoIva.reducer";
import { unidadesReducer } from "./store/reducers/unidades.reducer";
import { GlobalService } from "./core/services/global.service";
import { AuthService } from "./core/services/auth.service";
import { ReintentoInterceptor } from "./core/interceptors/reintento.interceptor";
import { esSinConexion } from "./core/utils/errores/errores-manejados"; // NUEVO

/**
 * Detección SÍNCRONA de soporte de push. Firebase Messaging requiere
 * serviceWorker + Notification + PushManager. Si algo falta, no registramos
 * el provider de Messaging para evitar el error de bootstrap
 * 'messaging/unsupported-browser'. Ver CP-NAV-021.
 */
function soportaMessaging(): boolean {
  return typeof window !== 'undefined'
      && typeof navigator !== 'undefined'
      && !!navigator.serviceWorker
      && typeof Notification !== 'undefined'
      && 'PushManager' in window;
}

registerLocaleData(localeEs);
export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes, withHashLocation(), withPreloading(CustomPreloadingStrategy)),
    provideHttpClient(
      withFetch(),
      withInterceptors([
        SpinnerInterceptor,
        UrlPatcherInterceptor(API_URL),
        HttpConfigInterceptor,
        ErrorHandlerInterceptor,
        ReintentoInterceptor,
      ]),
    ),
    provideStore({
      usuarioAuthState: usuarioAuthReducer,
      empresaActualState: empresaActualReducer,
      permisosState: permisosReducer,
      tipoResponsableivasState: ivaReducer,
      tipoDocumentosState: documentoReducer,
      paisesState: paisReducer,
      formaPagosState: formaPagosReducer,
      tipoComprobantesState: comprobanteReducer,
      parametrosGeneralesState: parametrosGeneralesReducer,
      tipoMonedasState: tipoMonedaReducer,
      provinciasState: provinciaReducer,
      actividadesState: actividadReducer,
      operacionesTipoIvaState: operacionTipoIvaReducer,
      unidadesState: unidadesReducer
    }),
    provideEffects(),
    { provide: LOCALE_ID, useValue: 'es' },
    provideAppInitializer(async  () => {
      const platformId = inject(PLATFORM_ID);
      const globalService = inject(GlobalService);
      const authService = inject(AuthService);

      if (isPlatformBrowser(platformId)) {
        const jwtToken = localStorage.getItem('jwtToken');
        if (jwtToken) {
          // FIX CP-GLB-020: validar/refrescar la sesión ANTES de cargar la config.
          // isAuthenticated() refresca el token si está vencido; el await garantiza
          // que setGlobalState salga siempre con token fresco y que el side-nav no
          // lea un selector en error durante el arranque.
          let sesionOk = false;
          try {
            sesionOk = await authService.isAuthenticated();
          } catch (err) {
            // NUEVO: el refresh falló por red. La sesión no venció: no se carga la config
            // y el AuthGuard de la navegación inicial vuelve a intentar el refresh (si sigue
            // sin conexión, muestra /sin-conexion). Sin este catch, una promesa rechazada en
            // un inicializador frena el arranque de toda la aplicación.
            if (!esSinConexion(err)) throw err;
          }
          if (sesionOk) {
            globalService.setGlobalState(jwtToken);
          }
          // Si no está ok, el AuthGuard de la navegación inicial desloguea y redirige al
          // login (isAuthenticated() / refreshToken() no desloguean por sí mismos).
        }
      }
    }),
    provideStoreDevtools({
      maxAge: 3,
      logOnly: !isDevMode(),
      autoPause: true,
      trace: false,
      traceLimit: 75,
      connectInZone: true,
    }),
    provideAnimationsAsync(),
    provideServiceWorker("ngsw-worker.js", {
      enabled: !isDevMode(),
      registrationStrategy: "registerWhenStable:30000",
    }),
    provideFirebaseApp(() => initializeApp(firebaseConfig)),
    // FIX CP-NAV-021: Messaging solo se provee si el navegador lo soporta.
    // En navegadores sin serviceWorker (iOS Safari sin PWA, etc.) getMessaging()
    // lanzaría 'messaging/unsupported-browser' en el bootstrap. Al no registrar
    // el provider, inject(Messaging, { optional: true }) devuelve null y el
    // navbar salta el bloque de notificaciones sin romper.
    ...(soportaMessaging() ? [provideMessaging(() => {
      try {
        return getMessaging();
      } catch {
        return null as any;  // o un stub
      }
    })] : []),
  ],
};